import { useEffect, useRef } from 'react';
import { onInstant } from '@/shared/api/websocket/realtime-sync.registry';
import { useUser } from '@/app/model/stores';
import type { ValidationItem } from './types';
import {
  decodeValidation,
  readUploadCompletedPayload,
  type UploadRowCopy,
} from './decode-validation';

interface UseValidationListenerProps {
  setValidationItems: React.Dispatch<React.SetStateAction<ValidationItem[]>>;
  /** Fallback row text when the server sent none. */
  copy: UploadRowCopy;
}

export function useValidationListener({
  setValidationItems,
  copy,
}: UseValidationListenerProps) {
  const user = useUser();

  // Read, never re-subscribed on — see useValidationPolling.
  const copyRef = useRef(copy);
  copyRef.current = copy;

  useEffect(() => {
    if (!user) return;

    // The intake announces itself once, with the outcome already settled
    // (docs/upload/ADR-002) — there is no earlier event to guard against.
    const cleanup = onInstant('uploads', (event) => {
      if (event.action !== 'completed') return;
      if (!event.id) return;

      const settled = readUploadCompletedPayload(event.payload);
      if (!settled) return;
      const { angleId } = settled;

      // One decoder for all three validation sources (initial / poll / WS) —
      // handles the lenient-vs-strict different-building split and every routed
      // outcome. See decode-validation.ts.
      const decoded = decodeValidation(settled.validation, copyRef.current);

      setValidationItems((prev) =>
        prev.map((item) =>
          item.id === event.id
            ? {
                ...item,
                status: decoded.status,
                note: decoded.note,
                error: decoded.error,
                validation: decoded.validation,
                angleId,
              }
            : item
        )
      );
    });

    return cleanup;
  }, [user, setValidationItems]);
}
